/*
  Atelier-Anfrage: sendet an Formspree (multipart), bis zu N Beispielbilder.
  Grosse Fotos werden vorher im Browser auf höchstens 2000px verkleinert (JPEG), damit der Versand leicht bleibt.
  Ohne JavaScript schickt das Formular direkt an Formspree.
*/
if (!customElements.get('d-atelier-form')) {
  class DamanoAtelierForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-form]');
      if (!this.form) return;
      this.max = parseInt(this.dataset.maxFiles, 10) || 5;
      this.files = [];
      try {
        this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
      } catch (e) {
        this.strings = {};
      }

      this.fileInput = this.querySelector('[data-file-input]');
      this.previews = this.querySelector('[data-previews]');
      this.drop = this.querySelector('[data-drop]');
      this.error = this.querySelector('[data-error]');
      this.idea = this.querySelector('[data-idea]');

      this.form.addEventListener('change', (event) => {
        if (event.target.name === 'Art' && event.target.dataset.placeholder !== undefined) {
          this.idea.placeholder = event.target.dataset.placeholder;
        }
      });

      this.fileInput.addEventListener('change', () => {
        this.addFiles(this.fileInput.files);
        this.fileInput.value = '';
      });

      ['dragenter', 'dragover'].forEach((type) =>
        this.drop.addEventListener(type, (event) => {
          event.preventDefault();
          this.drop.classList.add('is-over');
        })
      );
      ['dragleave', 'drop'].forEach((type) =>
        this.drop.addEventListener(type, () => this.drop.classList.remove('is-over'))
      );
      this.drop.addEventListener('drop', (event) => {
        event.preventDefault();
        this.addFiles(event.dataTransfer.files);
      });

      this.form.addEventListener('submit', (event) => this.submit(event));
      this.querySelector('[data-again]')?.addEventListener('click', () => this.reset());
    }

    /* ---------- Bilder ---------- */

    addFiles(list) {
      const images = [...(list || [])].filter((file) => file.type.startsWith('image/'));
      if (this.files.length + images.length > this.max) this.showError(this.strings.tooMany);
      images.slice(0, Math.max(0, this.max - this.files.length)).forEach((file) => this.files.push(file));
      this.renderPreviews();
    }

    renderPreviews() {
      this.previews.querySelectorAll('img').forEach((img) => URL.revokeObjectURL(img.src));
      this.previews.innerHTML = '';
      this.files.forEach((file, index) => {
        const li = document.createElement('li');
        li.className = 'd-aform__preview';
        const img = document.createElement('img');
        img.src = URL.createObjectURL(file);
        img.alt = file.name;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'd-aform__remove';
        remove.setAttribute('aria-label', `${this.strings.remove}: ${file.name}`);
        remove.textContent = '×';
        remove.addEventListener('click', () => {
          this.files.splice(index, 1);
          this.renderPreviews();
        });
        li.append(img, remove);
        this.previews.append(li);
      });
      this.drop.hidden = this.files.length >= this.max;
    }

    async shrink(file) {
      if (file.size < 1.5 * 1024 * 1024 || !('createImageBitmap' in window)) return file;
      try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, 2000 / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
        if (!blob) return file;
        return new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' });
      } catch (e) {
        return file;
      }
    }

    /* ---------- Senden ---------- */

    showError(message) {
      this.error.textContent = message || '';
      this.error.hidden = !message;
    }

    validate() {
      const name = this.form.elements['Name'];
      const email = this.form.elements['email'];
      const nameOk = name.value.trim() !== '';
      const emailOk = /\S+@\S+\.\S+/.test(email.value.trim());
      name.closest('.d-field').classList.toggle('d-field--error', !nameOk);
      email.closest('.d-field').classList.toggle('d-field--error', !emailOk);
      name.setAttribute('aria-invalid', String(!nameOk));
      email.setAttribute('aria-invalid', String(!emailOk));
      if (!nameOk) name.focus();
      else if (!emailOk) email.focus();
      return nameOk && emailOk;
    }

    async submit(event) {
      event.preventDefault();
      this.showError('');
      if (!this.validate()) {
        this.showError(this.strings.required);
        return;
      }

      const button = this.querySelector('[data-submit]');
      const label = button.textContent;
      button.disabled = true;
      button.textContent = this.strings.sending;

      const data = new FormData(this.form);
      data.delete('Bilder');
      for (const file of this.files) data.append('Bilder', await this.shrink(file));
      const subject = this.form.querySelector('[data-subject-base]');
      const kind = data.get('Art');
      data.set('_subject', `${subject.dataset.subjectBase}: ${kind || ''} – ${data.get('Name')}`.trim());

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: data,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const done = this.querySelector('[data-done]');
        this.querySelector('[data-done-title]').textContent = this.strings.thanksName.replace('[name]', data.get('Name'));
        this.form.hidden = true;
        done.hidden = false;
        done.focus();
      } catch (error) {
        console.error(error);
        this.showError(this.strings.failed);
      } finally {
        button.disabled = false;
        button.textContent = label;
      }
    }

    reset() {
      this.form.reset();
      this.files = [];
      this.renderPreviews();
      this.showError('');
      this.form.hidden = false;
      this.querySelector('[data-done]').hidden = true;
      const first = this.form.querySelector('input[name="Art"]:checked');
      if (first) this.idea.placeholder = first.dataset.placeholder || '';
    }
  }

  customElements.define('d-atelier-form', DamanoAtelierForm);
}
